// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { useDebtForm } from '@client/banks/hooks/use-debt-form';
import { BankDebtProductType, type BankDebt } from '@client/banks/types/debt';
import {
  createEmptyBankDebt,
  getDefaultBankCurrency,
} from '@client/banks/utils/bank-form-defaults';
import { commitInstrumentWithLinkedAccount } from '@client/banks/utils/commit-instrument-with-linked-account';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo, useRef, useState } from 'react';

export const useDebtsForm = () => {
  const { navigate } = useStepsData();
  const {
    formData,
    listEditor,
    currencyOptions,
    accountIbanOptions,
    replaceFormData,
    closeListEditor,
  } = useBankUploadData();

  const linkedLiquidityApiRef = useRef<LinkedLiquiditySectionApi | null>(null);

  const [linkedLiquidityGate, setLinkedLiquidityGate] =
    useState<LinkedLiquidityGate>({
      isCreateMode: false,
      isAccountFormValid: true,
    });

  const debts = useMemo(() => formData.debts ?? [], [formData.debts]);

  const defaultCurrency = useMemo(
    () => getDefaultBankCurrency(currencyOptions),
    [currencyOptions],
  );

  const defaultValues = useMemo(() => {
    const emptyDebt = createEmptyBankDebt(defaultCurrency);

    if (listEditor?.section !== 'debts') {
      return emptyDebt;
    }

    if (listEditor.index < debts.length) {
      const debt = debts[listEditor.index];

      return {
        ...debt,
        amortizations:
          debt.productType === BankDebtProductType.Loan
            ? (debt.amortizations ?? []).map(amortization => ({
                ...amortization,
              }))
            : [],
      };
    }

    return emptyDebt;
  }, [defaultCurrency, debts, listEditor]);

  const form = useDebtForm({
    variant: 'create',
    defaultValues,
  });

  const isFormValid = useCallback(() => {
    if (!form.formState.isValid) {
      return false;
    }

    if (
      linkedLiquidityGate.isCreateMode &&
      !linkedLiquidityGate.isAccountFormValid
    ) {
      return false;
    }

    return true;
  }, [form.formState.isValid, linkedLiquidityGate]);

  const onSubmit = useCallback(
    async (data: BankDebt) => {
      if (!listEditor || listEditor.section !== 'debts') {
        return;
      }

      const linkedResult = await linkedLiquidityApiRef.current?.resolveSubmit();

      if (!linkedResult || linkedResult.mode === 'invalid') {
        return;
      }

      const isLoanDebt = data.productType === BankDebtProductType.Loan;
      const amortizations = isLoanDebt
        ? data.amortizations?.filter(row => row.installmentDate.trim().length > 0)
        : undefined;

      const pendingAmount =
        data.productType === BankDebtProductType.CreditLine
          ? data.disposed ?? data.pendingAmount
          : data.pendingAmount;

      const nextFormData = commitInstrumentWithLinkedAccount(
        formData,
        'debts',
        listEditor.index,
        {
          ...data,
          pendingAmount,
          amortizations:
            amortizations && amortizations.length > 0 ? amortizations : undefined,
          iban: linkedResult.mode === 'create' ? '' : linkedResult.iban,
        },
        linkedResult.mode === 'create' ? linkedResult.account : undefined,
      );

      replaceFormData(nextFormData);
      closeListEditor();
      navigate('debts-list');
    },
    [closeListEditor, formData, listEditor, navigate, replaceFormData],
  );

  const onCancel = useCallback(() => {
    closeListEditor();
    navigate('debts-list');
  }, [closeListEditor, navigate]);

  return {
    form,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange: setLinkedLiquidityGate,
    isFormValid,
    onSubmit,
    onCancel,
  };
};
